import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, find, map, switchMap, take } from 'rxjs/operators';
import { OneqrewService } from '@oneqrew-partnertool/api';
import { selectCustomer } from '@oneqrew-partnertool/core';
import { Customer, Plan } from '@oneqrew-partnertool/models';

export const SUBCRIPTION_PLANS = {
  S_PAKET: {
    planType: 'smarthandwerk S-Paket',
    discount: 20,
    cloudVersion: false,
    packageType: 'pro-s',
    subDisplayName: 'S-Paket',
    name: 'Pro S'
  },
  M_PAKET: {
    planType: 'smarthandwerk M-Paket',
    discount: 20,
    cloudVersion: false,
    packageType: 'pro-m',
    subDisplayName: 'M-Paket',
    name: 'Pro M'
  },
  L_PAKET: {
    planType: 'smarthandwerk L-Paket',
    discount: 20,
    cloudVersion: false,
    packageType: 'pro-l',
    subDisplayName: 'L-Paket',
    name: 'Pro L'
  }
};

export const PLANS_HELPER = {
  isS_PAKET(value: string | undefined): boolean {
    return value == SUBCRIPTION_PLANS.S_PAKET.planType;
  },
  isM_PAKET(value: string | undefined): boolean {
    return value == SUBCRIPTION_PLANS.M_PAKET.planType;
  },
  isL_PAKET(value: string | undefined): boolean {
    return value == SUBCRIPTION_PLANS.L_PAKET.planType;
  },
  getName(packageType: String): string {
    return this.getByType(packageType)['name'] || 'Unknown Package';
  },
  getByType(packageType: String): any {
    return SUBCRIPTION_PLANS_LIST.find((s) => s.packageType == packageType);
  }
};

export const SUBCRIPTION_PLANS_LIST = [SUBCRIPTION_PLANS.S_PAKET, SUBCRIPTION_PLANS.M_PAKET, SUBCRIPTION_PLANS.L_PAKET];

@Injectable({
  providedIn: 'root'
})
export class PlanDataService {
  customer!: Customer;
  initialPlans = [SUBCRIPTION_PLANS.S_PAKET, SUBCRIPTION_PLANS.M_PAKET, SUBCRIPTION_PLANS.L_PAKET];

  constructor(private oneqrewService: OneqrewService, private store: Store) {}

  private getModuleCount(moduleName: string): number {
    return this.customer?.functionModules?.find((fm) => fm.name?.includes(moduleName))?.amount || 0;
  }

  getInitialPlans(): Observable<any[]> {
    return this.store.select(selectCustomer).pipe(
      take(1),
      switchMap((storedCustomer: Customer | null) => {
        if (!storedCustomer) {
          console.warn('Customer data not found in store.');
          return of([]); // Return an empty array if no customer data is available
        }

        this.customer = storedCustomer;

        // Prepare observables for each plan
        const planObservables = this.initialPlans.map((plan) => {
          // Calculate devicesCount based on packageType
          let devicesCount = Math.max(0, this.getModuleCount('Geräte'));
          if (plan.packageType === 'pro-l') {
            devicesCount = Math.max(0, devicesCount - 2);
          }
          // Calculate workStationCount and clientsCount
          const workStationCount = Math.max(0, this.getModuleCount('Arbeitsplätze') - 1);
          const clientsCount = Math.max(0, this.getModuleCount('mandant'));

          const updatedPlan = {
            ...plan,
            devicesCount,
            workStationCount,
            clientsCount
          };

          return this.oneqrewService.getProductByPackageTypeOneqrewProductPackage(plan.packageType).pipe(
            switchMap((productPackage) => {
              const price = productPackage?.monthlyPrice ?? 0;
              const planWithPrice = { ...updatedPlan, price };

              return this.oneqrewService.getModulesInProductPackageOneqrewModule(plan.packageType).pipe(
                map((modules) => {
                  const additionalModules = modules.filter((module) => module.isExtra).map(({ isExtra, ...module }) => module);
                  const regularModules = modules
                    .filter((module) => !module.isExtra && module.name !== 'Grundmodul')
                    .map(({ isExtra, ...module }) => module);
                  return { ...planWithPrice, modules: regularModules, additionalModules };
                })
              );
            }),
            catchError((error) => {
              console.error(`Error fetching data for ${plan.packageType}:`, error);
              return of({ ...updatedPlan, price: 0, modules: [], additionalModules: [] });
            })
          );
        });

        return forkJoin(planObservables);
      })
    );
  }
}
